import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Button, Description, Link, Row } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React from 'react';
import { CSSTransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { closeBanner } from '~shared/actions/banners';
import { selectUser } from '~shared/actions/ui/users';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import getAuth from '~shared/selectors/auth/getAuth';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useIsOnboardingRoute } from '~web/hooks';

type BackgroundColor = 'success' | 'error' | 'warning' | 'pink';
function backgroundColor(type: BackgroundColor) {
  switch (type) {
    case 'success':
      return COLORS.PALETTE.SUCCESS.LIGHTER;

    case 'error':
      return COLORS.PALETTE.DANGER.LIGHTEST;

    case 'warning':
      return COLORS.PALETTE.WARNING.LIGHTER;

    case 'pink':
      return COLORS.PALETTE.PINK.LIGHTER;

    default:
      return COLORS.PALETTE.PRIMARY.LIGHTER;
  }
}

const StyledRow = styled(Row)`
  width: 1140px;
`;

const StyledBanner = styled.div<{ type: BackgroundColor }>`
  background-color: ${({ type }) => backgroundColor(type)};
  width: 100%;
  padding: ${SPACING.STATIC.MD.px};
  display: flex;
  justify-content: center;
  align-items: center;
`;

type BannerType = 'ADD_INSURANCE';

type BannerProps = {
  onPress?: () => void;
  type: BackgroundColor;
  buttonText?: string;
  content: React.ReactNode;
  dismissible?: boolean;
};

function getBannerProps(
  bannerType: BannerType | undefined,
  buttonHandlers: Record<string, () => void>,
): BannerProps | undefined {
  switch (bannerType) {
    case 'ADD_INSURANCE':
      return {
        onPress: buttonHandlers.ADD_INSURANCE,
        type: 'warning' as BackgroundColor,
        buttonText: '+ Add Insurance',
        content: (
          <Row centerVertically>
            <Description color={COLORS.PALETTE.GREYSCALE.DARKER}>
              Speed up your checkout process by adding your insurance now!
            </Description>
          </Row>
        ),
      };

    default:
      return undefined;
  }
}

export const Banner = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { authorized, onboarding: authOnboarding } = useSelectorShared(getAuth);
  const { bannerType, showBanner } = useSelectorShared((state) => state.banners);
  const { isOnboardingRoute, isOrganicOnboardingRoute } = useIsOnboardingRoute();
  const onboarding = authOnboarding || isOnboardingRoute || isOrganicOnboardingRoute;
  const currentUserID = useSelectorShared(getCurrentUserID);
  const buttonHandlers = {
    ADD_INSURANCE: () => {
      if (!currentUserID) return;

      dispatch(selectUser(currentUserID));
      navigate('RouteInsuranceAdd', { source: 'banner', userID: currentUserID });
    },
  };

  const bannerProps = getBannerProps(bannerType, buttonHandlers);
  if (!bannerProps) return null;

  const { onPress, buttonText, content, dismissible = true, type } = bannerProps;
  let body = <div />;

  if (showBanner && authorized && !onboarding) {
    body = (
      <StyledBanner type={type}>
        <StyledRow
          center
          gap="SM"
        >
          {content}
          <Row
            centerVertically
            gap="SM"
          >
            {onPress && buttonText ? (
              <Button
                label={buttonText}
                accessibilityLabel={buttonText}
                type="secondary"
                onPress={onPress}
              />
            ) : null}
            {dismissible ? (
              <Link
                onPress={() => {
                  dispatch(closeBanner());
                }}
              >
                <span>&times;</span>
              </Link>
            ) : null}
          </Row>
        </StyledRow>
      </StyledBanner>
    );
  }

  return (
    <CSSTransitionGroup
      transitionAppear
      transitionName="banner"
      transitionAppearTimeout={200}
      transitionEnterTimeout={200}
      transitionLeaveTimeout={200}
    >
      {body}
    </CSSTransitionGroup>
  );
};
